import { navigatorClient } from "@/api";
import type { ApiResponse } from "@/types";

import type { FinderTag } from "./finder-service";
import type { InternalDetailData } from "./internal-detail-types";

// F01-1 에이전트 상세 (backend /finder/agents/{id}, 설계서 v0.5). 서버가 제출 단계(2차까지 · 1차+WF · 1차만)와 회원 유형에 맞게 거른 값을 주고,
// 값이 없는 섹션 · 요소는 null · 빈 목록 → 화면이 요소 · 바로가기 탭째 숨겨요.

export interface LabelValue {
  label: string;
  value: string;
}

export interface WorkflowStep {
  code: string;
  name: string;
  hitl: string | null;
  does: string | null;
  input: string | null;
  output: string | null;
  // 이어지는 세부단계 번호 · 되돌아가는 세부단계 번호
  next: string[];
  loop_back: string[];
  // 2차까지: To-Be 단계와 짝지은 '도입 후 처리 내용'
  after: string | null;
}

export interface WorkflowStage {
  no: number;
  name: string;
  description: string | null;
  steps: WorkflowStep[];
}

export interface DetailOverview {
  // 2차까지: 해결하는 문제(Before) 큰 문장 / 1차+WF · 1차만: 개요 첫 문장(90자 이하) 리드
  headline: string | null;
  lead: string | null;
  paragraphs: string[];
  strength: string | null;
  data_modalities: string[];
  interfaces: string[];
  // 도입 대상 3카드 (2차까지)
  audience: LabelValue[];
  differentiators: { name: string | null; description: string | null }[];
}

export interface DetailFeatures {
  // features = 2차까지 주요 기능 / components = 1차+WF Component 행 / differentiators = 1차만 차별화 포인트
  kind: "features" | "components" | "differentiators";
  items: { name: string | null; description: string | null; tags: string[] }[];
  // 구성 컴포넌트 카드 (2차까지 전용)
  components: { no: string; name: string; role: string | null; tags: string[] }[];
}

export interface DetailMetricRow {
  label: string;
  before: string | null;
  after: string | null;
  // 개선 칩 문구 (`약 90% 단축`) — 도입 전 · 후가 모두 있고 계산이 되는 항목에만
  delta: string | null;
}

export interface DetailMetric {
  name: string;
  headline: boolean;
  before: string | null;
  after: string | null;
  delta: string | null;
  description: string | null;
  period: string | null;
  // ver.7: `A / B / C` 복합 지표는 항목별 행 (비면 단일 지표), 확정 전 목표치 여부
  rows: DetailMetricRow[];
  target: boolean;
}

export interface DetailTrust {
  pills: { label: string; state: "verified" | "supported" }[];
  verified: number;
  supported: number;
  certifications: string[];
  summary: string | null;
  elements: { label: string | null; text: string }[];
  product_certifications: string[];
}

export interface DetailWorkflow {
  title: string;
  stages: WorkflowStage[];
  hitl_chips: string[];
  hitl_note: string | null;
  inputs: { name: string; format: string | null; source: string | null; required: string | null }[];
  data_collection: string | null;
  outputs: { name: string; format: string | null; content: string | null }[];
  integrations: string | null;
  before: {
    summary?: string | null;
    steps?: { stage: string | null; items: { no: number | null; title: string | null; text: string }[] }[];
    pain_points?: string[];
    problem?: string | null;
    cards?: string[];
  } | null;
  after: {
    automation?: string | null;
    agent_scope?: string | null;
    human_scope?: string | null;
    changes?: { label: string | null; text: string }[];
    solution?: string | null;
  } | null;
}

export interface DetailUsecase {
  label: string | null;
  internal_only: boolean;
  title: string;
  content: string | null;
  effect: string | null;
}

export interface DetailAdoption {
  tiles: { key: string; label: string; text: string | null; link: string | null; link_label: string | null }[];
  requirements: { title: string; body: string }[];
  playground_url: string | null;
}

export interface DetailComparison {
  agents: { id: string; name: string; is_current: boolean; detail_type: "external" | "internal"; supplier_name: string | null }[];
  // 셀은 줄 목록
  rows: { label: string; values: string[][] }[];
}

export interface AgentDetailSections {
  overview: DetailOverview | null;
  features: DetailFeatures | null;
  // target: 모든 지표가 확정 전 목표치 → 제목 옆 '목표치 · 확정 전' 칩
  metrics: { items: DetailMetric[]; condition: string | null; target: boolean } | null;
  trust: DetailTrust | null;
  workflow: DetailWorkflow | null;
  usecases: DetailUsecase[] | null;
  adoption: DetailAdoption | null;
  comparison: DetailComparison | null;
}

export interface DetailSupplier {
  id: string;
  name: string;
  company_type: string | null;
  homepage: string | null;
  homepage_label: string | null;
  logo_url: string | null;
  cert_badge: boolean;
  company_size: string | null;
  customer_scale: string | null;
  location: string | null;
  assets: string[];
  other_products: { id: string; name: string; summary: string | null; agent_type: string | null; detail_type: "external" | "internal" }[];
  other_products_total: number;
  // PwC 내부에게만: 보유 인증 · 자금 조달 · 매출 규모 · 산업별 대표 고객사
  internal: {
    certifications: string[];
    funding_stage: string | null;
    revenue_range: string | null;
    reference_customers: { industry: string | null; customers: string }[];
  } | null;
}

export interface AgentDetail {
  id: string;
  aid: string | null;
  name: string;
  detail_type: "external" | "internal";
  // 보는 사람의 회원 유형 (수요자 · 공급자 · PwC 내부)
  audience: "demand" | "supplier" | "internal" | "guest" | "pending";
  is_certified: boolean;
  summary: string | null;
  modifier: string | null;
  breadcrumb: { label: string | null; industry?: string; industry_detail?: string | null };
  supplier: DetailSupplier | null;
  classification: {
    industry: string | null;
    industries?: string | null; // 산업만 (내부 에이전트 상세)
    tasks?: string | null; // 업무만 (내부 에이전트 상세)
    agent_type: FinderTag | null;
    deployment: FinderTag | null;
    release_status: string | null;
  };
  media: { demo_video_url: string | null; brochure_urls: string[] };
  is_bookmarked: boolean;
  in_compare: boolean;
  headline_metric: { label: string; value: string } | null;
  tech_stack: string[];
  // 내부 Asset 상세(Figma ver.7) 값 묶음 — 타입은 internal-detail-types.ts
  internal: InternalDetailData | null;
  sections: AgentDetailSections;
}

export async function getAgentDetail(id: string, source?: string, signal?: AbortSignal) {
  const { data } = await navigatorClient.get<ApiResponse<AgentDetail>>(`/finder/agents/${id}`, { params: source ? { source } : undefined, signal });
  return data.data!;
}

export type DefinitionFormat = "pdf" | "html" | "md";

export interface AgentDefinition {
  agent_id: string;
  agent_name: string;
  title: string;
  version: string;
  updated_at: string | null;
  breadcrumb_industry: string | null;
  toc: { id: string; title: string }[];
  html: string;
  markdown: string;
  file_name: string;
}

export async function getAgentDefinition(id: string, download?: DefinitionFormat, signal?: AbortSignal) {
  const { data } = await navigatorClient.get<ApiResponse<AgentDefinition>>(`/finder/agents/${id}/definition`, {
    params: download ? { download } : undefined,
    signal,
  });
  return data.data!;
}

// 정의서 내려받기: HTML · Markdown은 파일로, PDF는 인쇄 화면(PDF로 저장)으로 (가정 A45)
const PRINT_STYLE = `
  body { font-family: "Noto Sans KR", sans-serif; color: #1A1C21; max-width: 760px; margin: 40px auto; line-height: 1.6; }
  .eyebrow { color: #B84000; font: 700 11px Inter, sans-serif; letter-spacing: .04em; margin: 0; }
  h1 { font-size: 22px; margin: 8px 0 4px; } h2 { font-size: 16px; margin: 28px 0 10px; } h3 { font-size: 14px; margin: 16px 0 6px; }
  .meta { color: #6A6F77; font-size: 12px; } p, li { font-size: 13px; color: #3B3B3B; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; } th, td { border: 1px solid #E6E6E3; padding: 10px 14px; text-align: left; vertical-align: top; }
  th { width: 160px; background: #F5F5F2; color: #5E626A; font-size: 12px; } .empty { color: #6A6F77; }
`;

function escapeHtml(text: string) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// 제목은 에이전트 이름이라 이스케이프하고(본문 def.html은 백엔드가 이미 이스케이프), 문서 안 스크립트는 CSP로 막아요.
export function definitionHtmlDocument(def: AgentDefinition) {
  return `<!doctype html><html lang="ko"><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="script-src 'none'"><title>${escapeHtml(def.title)}</title><style>${PRINT_STYLE}</style></head><body>${def.html}</body></html>`;
}

function saveFile(content: string, fileName: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadDefinition(agentId: string, format: DefinitionFormat) {
  const def = await getAgentDefinition(agentId, format);
  if (format === "md") return saveFile(def.markdown, `${def.file_name}.md`, "text/markdown;charset=utf-8");
  if (format === "html") return saveFile(definitionHtmlDocument(def), `${def.file_name}.html`, "text/html;charset=utf-8");
  const frame = document.createElement("iframe");
  // 스크립트는 실행하지 않고(allow-scripts 없음) 인쇄만 허용 — 부모가 문서를 쓰도록 같은 출처는 유지
  frame.setAttribute("sandbox", "allow-same-origin allow-modals");
  frame.style.position = "fixed";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);
  const doc = frame.contentWindow?.document;
  if (!doc) return frame.remove();
  doc.open();
  doc.write(definitionHtmlDocument(def));
  doc.close();
  frame.contentWindow?.focus();
  frame.contentWindow?.print();
  setTimeout(() => frame.remove(), 60_000);
}
